import { http } from '@/lib/axios';
import { unwrap, toApiError } from '@/lib/unwrap';
import type { ApiEnvelope } from '@/types/api';
import { uuidv4 } from '@/lib/uuid';

/**
 * wristband.api — ticketbox wrappers for the public /qr/* + /sale/addon surface.
 *
 * Ported from autopia-kiosk (src/service/api/ab-pos/wristband.js). The AP2
 * wristband token's HMAC signature IS the identity for /qr/resolve and
 * /qr/transfer-addon — the BE verifies it cryptographically and resolves the
 * row by the signed `qid`; no operator context is needed for those. The
 * ticketbox axios instance still carries the cashier JWT, which is harmless
 * here and required for /sale/addon (cashier-attributed POS add-on sale).
 */

// ── Types ────────────────────────────────────────────────────────────────

export interface WristbandAddOn {
  /** The addOns[] subdoc _id — the reschedule key (absent on legacy add-ons). */
  _id?: string;
  saleItemId?: string;
  productId?: string;
  productName?: string;
  name?: string;
  attractionId?: string | null;
  quantity?: number;
  quantityIndex?: number;
  slotId?: string | null;
  slotStartAt?: string | null;
  slotEndAt?: string | null;
  status?: string;
  /** Set when the add-on was refunded — it has released its purchase-cap quota. */
  refundedAt?: string | null;
  isEntered?: boolean;
  isScanned?: boolean;
  origin?: string;
  transferHistory?: unknown[];
  [k: string]: unknown;
}

export interface ResolvedWristband {
  qrCode?: {
    _id?: string;
    value?: string;
    type?: string;
    effectiveDate?: string | null;
    label?: string | null;
    isEntered?: boolean;
    isScanned?: boolean;
    parentSlot?: { slotId?: string; slotStartAt?: string | null; slotEndAt?: string | null } | null;
    addOns?: WristbandAddOn[];
    itinerary?: unknown;
  };
  transferChallenge?: string | null;
  transferChallengeTtlSeconds?: number;
  parentSaleItem?: {
    _id?: string;
    productId?: string;
    productName?: string;
    slotId?: string | null;
    slotStartAt?: string | null;
    slotEndAt?: string | null;
  };
  sale?: { _id?: string; invoiceId?: string; visitDate?: string; status?: string };
  [k: string]: unknown;
}

export interface AddOnRef {
  saleItemId: string;
  productId?: string;
  quantityIndex: number;
}

export interface TransferResult {
  transferredCount?: number;
  [k: string]: unknown;
}

export interface AddOnUnitSchedule {
  slotId: string | null;
}
export interface CreateAddOnItem {
  productId: string;
  quantity: number;
  unitSchedules: AddOnUnitSchedule[];
}
export interface CreateAddOnSalePayload {
  qrToken: string;
  paymentMethodId: string;
  addOnItems: CreateAddOnItem[];
  couponCode?: string;
  /** POS cash flow — the cashier collected physical cash; the BE records it and
   *  derives change. Optional (omit/null for non-cash tenders). */
  amountTendered?: number | null;
}

// ── Calls ────────────────────────────────────────────────────────────────

/** Resolve a scanned AP1/AP2 wristband token to its ticket context. */
export async function resolveWristband(qrToken: string): Promise<ResolvedWristband> {
  try {
    const res = await http.post<ApiEnvelope<ResolvedWristband>>('/qr/resolve', { qrToken });
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Transfer N AVAILABLE add-on saleItems from one wristband to another within
 * the same Sale, atomically. Dedupes on the full composite key so the BE's
 * DUPLICATE_ADDON_REF guard is never tripped.
 */
export async function transferAddOn(args: {
  fromQrToken: string;
  toQrToken: string;
  addOnRefs: AddOnRef[];
  fromChallenge?: string | null;
  toChallenge?: string | null;
}): Promise<TransferResult> {
  const { fromQrToken, toQrToken, addOnRefs, fromChallenge, toChallenge } = args;
  if (!fromQrToken || !toQrToken) {
    throw toApiError(new Error('fromQrToken and toQrToken are required'));
  }

  const seen = new Set<string>();
  const deduped: AddOnRef[] = [];
  (addOnRefs ?? []).forEach((r) => {
    if (!r || !r.saleItemId || !Number.isFinite(Number(r.quantityIndex))) return;
    const saleItemId = String(r.saleItemId);
    const quantityIndex = Number(r.quantityIndex);
    const productId = r.productId ? String(r.productId) : undefined;
    const key = `${saleItemId}::${productId ?? '_'}::${quantityIndex}`;
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(productId ? { saleItemId, productId, quantityIndex } : { saleItemId, quantityIndex });
  });
  if (deduped.length === 0) {
    throw toApiError(new Error('Pilih minimal satu add-on untuk ditransfer.'));
  }

  const body: Record<string, unknown> = { fromQrToken, toQrToken, addOnRefs: deduped };
  if (fromChallenge) body.fromChallenge = fromChallenge;
  if (toChallenge) body.toChallenge = toChallenge;

  try {
    const res = await http.post<ApiEnvelope<TransferResult>>('/qr/transfer-addon', body);
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Create a follow-up Sale that ATTACHES new add-on children to an existing
 * parent QR. Returns the same CreateSaleResult shape as /sale so the ticketbox
 * payment flow (cash / e-money / QRIS) can drive it identically.
 */
export async function createAddOnSale(
  payload: CreateAddOnSalePayload,
  idempotencyKey?: string,
): Promise<Record<string, unknown>> {
  try {
    const res = await http.post<ApiEnvelope<Record<string, unknown>>>('/sale/addon', payload, {
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    });
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

// ── Pure helpers (ported verbatim from the kiosk) ──────────────────────────

/**
 * Crude AP1/AP2 shape gate — does NOT verify the signature (only the BE has the
 * HMAC key). A fast UX gate before posting so a garbage scan gives an instant
 * "that's not a wristband code" hint. Crockford alphabet: digits + A-Z − I/L/O/U.
 */
export function looksLikeWristbandToken(raw: unknown): boolean {
  if (typeof raw !== 'string') return false;
  const trimmed = raw.trim();
  if (/^AP2\.[0-9A-HJ-NP-TV-Za-hj-np-tv-z]+\.[0-9A-HJ-NP-TV-Za-hj-np-tv-z]+$/.test(trimmed)) return true;
  if (/^AP1\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(trimmed)) return true;
  return false;
}

/** Pull the short-lived transfer challenge off a resolve() payload. */
export function pickTransferChallenge(resolved: ResolvedWristband | null | undefined): string | null {
  if (!resolved || typeof resolved !== 'object') return null;
  return (resolved.transferChallenge as string) || null;
}

/**
 * Normalize the add-on children off a resolved wristband payload. Guarantees
 * every row carries `origin` + `transferHistory` so consumers don't null-check.
 */
export function resolveAddOnsForQr(resolved: ResolvedWristband | null | undefined): WristbandAddOn[] {
  const qr = resolved?.qrCode;
  const parentSaleItem = resolved?.parentSaleItem;
  const withProvenance = (a: WristbandAddOn): WristbandAddOn => {
    if (!a || typeof a !== 'object') return a;
    return {
      ...a,
      origin: a.origin || 'PURCHASE',
      transferHistory: Array.isArray(a.transferHistory) ? a.transferHistory : [],
    };
  };
  if (Array.isArray(qr?.addOns) && qr!.addOns!.length > 0) return qr!.addOns!.map(withProvenance);
  const psAddOns = (parentSaleItem as { addOnItems?: WristbandAddOn[] } | undefined)?.addOnItems;
  if (Array.isArray(psAddOns) && psAddOns.length > 0) {
    const expanded: WristbandAddOn[] = [];
    psAddOns.forEach((a) => {
      const qty = Math.max(Number(a?.quantity) || 1, 1);
      for (let i = 0; i < qty; i++) {
        expanded.push(
          withProvenance({
            ...a,
            productName:
              (a as { productDetail?: { name?: string } }).productDetail?.name ||
              a.productName ||
              a.name,
            quantityIndex: i + 1,
          }),
        );
      }
    });
    return expanded;
  }
  return [];
}

/**
 * Stable composite identity for an add-on row. The qrCode.addOns[] sub-doc is
 * `_id: false` and a qty>1 saleItem fans into N rows sharing saleItemId, so
 * saleItemId alone is not a row id — key on the natural composite.
 */
export function makeAddOnCompositeId(a: WristbandAddOn | null | undefined, idx = 0): string {
  if (!a || typeof a !== 'object') return `addon-${idx}`;
  const part = (v: unknown) => (v == null || v === '' ? '_' : String(v));
  return [
    part(a.saleItemId),
    part(a.productId),
    part(a.attractionId),
    part(a.slotId),
    part(a.quantityIndex ?? idx),
  ].join('::');
}

/** An add-on is transferable when it isn't used/scanned/refunded and its slot hasn't ended. */
export function isAddOnTransferable(a: WristbandAddOn | null | undefined, nowMs = Date.now()): boolean {
  if (!a) return false;
  const used =
    a.isEntered === true ||
    a.isScanned === true ||
    a.status === 'USED' ||
    a.status === 'ENTERED' ||
    a.status === 'SCANNED' ||
    a.status === 'REFUNDED';
  const ended = !!a.slotEndAt && new Date(a.slotEndAt).getTime() < nowMs;
  return !used && !ended;
}

/**
 * Reschedulable = transferable AND it has a real slot to move (slotId) AND a
 * subdoc `_id` (the reschedule key; legacy add-ons synthesized from the Mixed
 * `addOnItems[]` snapshot have none and can't be rescheduled).
 */
export function isAddOnReschedulable(a: WristbandAddOn | null | undefined, nowMs = Date.now()): boolean {
  return isAddOnTransferable(a, nowMs) && !!a?._id && !!a?.slotId;
}

/**
 * Reschedule ONE add-on to a different SAME-DATE slot of the same product.
 * Reuses the admin reschedule endpoint (which enforces `ticket.reschedule.addon`
 * + audits the actor), so it must be called with a MANAGER's access token —
 * obtained via a credential override at the POS. Same-date only; the BE rejects
 * a cross-date target.
 */
export async function rescheduleAddOn(
  body: {
    saleId: string;
    parentQrId: string;
    addOnId: string;
    targetSlotId: string;
    reason?: string;
  },
  managerToken: string,
): Promise<unknown> {
  try {
    const res = await http.post<ApiEnvelope<unknown>>('/admin/sale/reschedule/addon', body, {
      headers: {
        Authorization: `Bearer ${managerToken}`,
        'Idempotency-Key': uuidv4(),
      },
    });
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}
